import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20 } from "styled-components";
import dynamic from "next/dynamic";
import React from "react";
import { type SceneSelections } from "~/features/product-configurator/3d/configurator-scene";
import { ConfiguratorAccessoryGroup } from "~/features/product-configurator/configurator-accessory-group";
import { ACCESSORY_GROUPS, ACCORDION_ITEMS, buildInitialSelections, buildSceneSelections } from "~/features/product-configurator/configurator-data";
import { ConfiguratorOthersAccordion } from "~/features/product-configurator/configurator-others-accordion";
import { ConfiguratorSpecsList } from "~/features/product-configurator/configurator-specs-list";
import { DEFAULT_CAP_BY_SENSOR, type SENSOR_MODEL_BY_SELECTION } from "~/features/product-configurator/utils/scene-models";
import { useDebugMode } from "~/features/product-configurator/utils/useDebugMode";
import { Button } from "~/features/ui/button";
import { Icon } from "~/features/ui/icon";
import { Tag } from "~/features/ui/tag";
import { Wrap } from "~/features/ui/wrap";
import Plus from "~/public/icons/plus.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";
const ConfiguratorScene = dynamic(() => import("~/features/product-configurator/3d/configurator-scene").then(mod => mod.ConfiguratorScene), {
  ssr: false
});
type Props = {
  title?: string;
  caption?: string;
  tag?: string;
  highlights?: string[];
  baseModel?: keyof typeof SENSOR_MODEL_BY_SELECTION;
  cta?: React.ReactNode;
};
export function ProductConfigurator({
  title,
  caption,
  tag,
  highlights,
  baseModel,
  cta
}: Props) {
  const isDebugMode = useDebugMode();
  const [openAccordion, setOpenAccordion] = React.useState("");
  const [selections, setSelections] = React.useState<Record<string, string>>(() => {
    const defaults = buildInitialSelections();
    if (baseModel) {
      defaults["sensors"] = baseModel;
      const cap = DEFAULT_CAP_BY_SENSOR[baseModel];
      if (cap) {
        defaults["caps"] = cap;
      } else {
        delete defaults["caps"];
      }
    }
    return defaults;
  });
  const sceneSelections: SceneSelections = React.useMemo(() => buildSceneSelections(selections), [selections]);
  const handleSelect = (groupId: string, optionId: string) => {
    setSelections(prev => ({
      ...prev,
      [groupId]: prev[groupId] === optionId ? "" : optionId
    }));
  };
  return <_StyledSection data-theme="dark">
      <Wrap mode="dark">
        <_StyledDiv>
          <_StyledDiv2>
            {tag && <div>
                <Tag variant="solid-blue">{tag}</Tag>
              </div>}

            {title && <_StyledH>
                {title}
                {caption && <_StyledSpan> {caption}</_StyledSpan>}
              </_StyledH>}

            {caption && <_StyledP>{caption}</_StyledP>}

            {cta && <_StyledDiv3>{cta}</_StyledDiv3>}
          </_StyledDiv2>

          <_StyledDiv4>
            <_StyledDiv5 $_css2={_css(["", " background-image:radial-gradient(circle,rgba(255,255,255,1) 0.8px,transparent 0.8px);background-size:20px 20px;"], {
            "pointerEvents": "none",
            "position": "absolute",
            "inset": "0",
            "zIndex": "10"
          })} />
            <_StyledDiv6 $_css3={_css(["", " background:radial-gradient(ellipse at center,transparent -60%,rgba(0,7,4,1) 60%);"], {
            "pointerEvents": "none",
            "position": "absolute",
            "inset": "0",
            "zIndex": "20"
          })} />

            <ConfiguratorScene selections={sceneSelections} />

            <_StyledDiv7>
              DRAG TO EXPLORE
              <Icon icon={Plus} size="12" />
            </_StyledDiv7>
          </_StyledDiv4>

          <_StyledDiv8 $_css4={[{
          "width": "100%",
          "backgroundColor": "var(--bg-primary)",
          "padding": "var(--spacing-md)",
          "@media (min-width: 1024px)": {
            "width": "30%",
            "padding": "var(--spacing-xl)",
            "paddingTop": "calc(var(--header-height) + var(--announcement-bar-height) + var(--spacing-xl))"
          }
        }, {
          "display": "flex",
          "flexDirection": "column"
        }]}>
            <_StyledDiv9>
              {tag && <div>
                  <Tag variant="solid-blue">{tag}</Tag>
                </div>}

              {title && <_StyledH2>
                  {title}
                  {caption && <_StyledSpan2> {caption}</_StyledSpan2>}
                </_StyledH2>}

              {caption && <_StyledP2>{caption}</_StyledP2>}

              {cta && <_StyledDiv10>{cta}</_StyledDiv10>}
            </_StyledDiv9>

            <ConfiguratorSpecsList highlights={highlights ?? []} />

            {ACCESSORY_GROUPS.map(group => {
            if (group.id === "sensors" && !isDebugMode) return null;
            if (group.id === "caps" && !isDebugMode) {
              const selectedCap = selections[group.id];
              if (!selectedCap) return null;
              const selectedOption = group.options.find(o => o.id === selectedCap);
              if (!selectedOption) return null;
              return <ConfiguratorAccessoryGroup key={group.id} group={{
                ...group,
                options: [selectedOption]
              }} selectedOptionId={selectedCap} onSelectOption={optionId => handleSelect(group.id, optionId)} />;
            }
            return <ConfiguratorAccessoryGroup key={group.id} group={group} selectedOptionId={selections[group.id]} onSelectOption={optionId => handleSelect(group.id, optionId)} />;
          })}

            <_StyledDiv11>
              <_StyledButton text="Request Quote" variant="secondary" icon={ArrowRight} fullWidth />
              <_StyledDiv12>
                <Button text="Compare Sensors" variant="underline" icon={ArrowRight} />
              </_StyledDiv12>
            </_StyledDiv11>

            <ConfiguratorOthersAccordion items={ACCORDION_ITEMS} value={openAccordion} onValueChange={setOpenAccordion} />
          </_StyledDiv8>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = _styled("section").withConfig({
  componentId: "sc-sf-qs8d6p-0"
})({
  "position": "relative",
  "color": "var(--text-primary)"
});
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-qs8d6p-1"
})({
  "display": "flex",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "flexDirection": "row"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-qs8d6p-2"
})({
  "position": "relative",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-md)",
  "backgroundColor": "var(--bg-primary)",
  "paddingLeft": "var(--spacing-md)",
  "paddingRight": "var(--spacing-md)",
  "paddingTop": "calc(var(--header-height) + var(--announcement-bar-height) + var(--spacing-xl))",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledH = _styled4("h1").withConfig({
  componentId: "sc-sf-qs8d6p-3"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledSpan = _styled5("span").withConfig({
  componentId: "sc-sf-qs8d6p-4"
})({
  "color": "var(--text-transparent)"
});
var _StyledP = _styled6("p").withConfig({
  componentId: "sc-sf-qs8d6p-5"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv3 = _styled7("div").withConfig({
  componentId: "sc-sf-qs8d6p-6"
})({
  "marginTop": "var(--spacing-lg)"
});
var _StyledDiv4 = _styled8("div").withConfig({
  componentId: "sc-sf-qs8d6p-7"
})({
  "position": "relative",
  "height": "60vh",
  "width": "100%",
  "@media (min-width: 1024px)": {
    "position": "sticky",
    "top": "calc(var(--header-height) + var(--announcement-bar-height))",
    "height": "calc(100vh - var(--header-height) - var(--announcement-bar-height))",
    "width": "70%",
    "alignSelf": "flex-start"
  }
});
var _StyledDiv5 = _styled9("div").withConfig({
  componentId: "sc-sf-qs8d6p-8"
})`${p => p.$_css2}`;
var _StyledDiv6 = _styled10("div").withConfig({
  componentId: "sc-sf-qs8d6p-9"
})`${p => p.$_css3}`;
var _StyledDiv7 = _styled11("div").withConfig({
  componentId: "sc-sf-qs8d6p-10"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "bottom": "var(--spacing-xl)",
  "left": "var(--spacing-xl)",
  "zIndex": "20",
  "display": "flex",
  "height": "4rem",
  "alignItems": "center",
  "gap": "0.8rem",
  "borderRadius": "2px",
  "backgroundColor": "var(--bg-transparent-20)",
  "paddingLeft": "var(--spacing-md)",
  "paddingRight": "var(--spacing-md)",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "textTransform": "uppercase",
  "--tw-backdrop-blur": "blur(16px)",
  "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledDiv8 = _styled12("div").withConfig({
  componentId: "sc-sf-qs8d6p-11"
})`${p => p.$_css4}`;
var _StyledDiv9 = _styled13("div").withConfig({
  componentId: "sc-sf-qs8d6p-12"
})({
  "display": "none",
  "height": "100%",
  "flexDirection": "column",
  "gap": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "display": "flex"
  }
});
var _StyledH2 = _styled14("h1").withConfig({
  componentId: "sc-sf-qs8d6p-13"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledSpan2 = _styled15("span").withConfig({
  componentId: "sc-sf-qs8d6p-14"
})({
  "color": "var(--text-transparent)"
});
var _StyledP2 = _styled16("p").withConfig({
  componentId: "sc-sf-qs8d6p-15"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv10 = _styled17("div").withConfig({
  componentId: "sc-sf-qs8d6p-16"
})({
  "marginTop": "var(--spacing-lg)"
});
var _StyledDiv11 = _styled18("div").withConfig({
  componentId: "sc-sf-qs8d6p-17"
})({
  "marginTop": "var(--spacing-md)",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-md)"
});
var _StyledButton = _styled19(Button).withConfig({
  componentId: "sc-sf-qs8d6p-18"
})({
  "justifyContent": "center"
});
var _StyledDiv12 = /*#__PURE__*/_styled20("div").withConfig({
  displayName: "product-configurator___StyledDiv12",
  componentId: "sc-dsjgao-0"
})({
  "display": "flex",
  "justifyContent": "center"
});